import { useState, useRef, useEffect } from 'react'
import { ChevronDownThinIcon } from 'src/components/Icons/ChevronDownThinIcon'
import type { VariationValue } from 'src/components/product-page/Selector/types'
import { CheckSelectedIcon } from 'src/components/Icons/CheckSelectedIcon'
import { useClickOutside } from 'src/utils/useClickOutside'

export default function CustomSelect({
  onChange,
  options,
  auxiliaryText,
  hasDisabledButtons,
  origin = 'floating-bar',
}: Readonly<{
  onChange?: (option: VariationValue) => void
  options: VariationValue[]
  auxiliaryText?: string
  origin?: 'floating-bar' | 'sidebar'
  hasDisabledButtons?: boolean
}>) {
  const [isOpen, setIsOpen] = useState(false)
  const [selected, setSelected] = useState<VariationValue | undefined>()

  const selectRef = useRef<HTMLDivElement>(null)

  useClickOutside(selectRef, () => setIsOpen(false))

  const handleSelect = (option: VariationValue) => {
    setSelected(option)
    setIsOpen(false)
    onChange?.(option)
  }

  useEffect(() => {
    setSelected(options?.find((option) => option.selected))
  }, [options])

  const isSidebar = origin === 'sidebar'

  const triggerClassName = isSidebar
    ? 'flex justify-between items-center w-full px-4 rounded-none border border-lightGray h-16'
    : 'flex justify-between items-center w-full px-4 rounded-none border border-lightGray h-12'

  const triggerTextClassName = isSidebar
    ? 'text-[16px] leading-[24px] text-restructure-disabled'
    : 'text-font-size-4 text-restructure-disabled'

  const isSidebarSizeSelector =
    isSidebar && auxiliaryText?.toLowerCase().includes('tamanho')

  const triggerText = isSidebarSizeSelector
    ? selected?.name ?? 'Selecione o tamanho'
    : `${auxiliaryText ?? ''}${selected?.name ?? ''}`.trim()

  return (
    <div ref={selectRef} className="relative min-w-56">
      <button
        disabled={hasDisabledButtons}
        onClick={() => setIsOpen((prev) => !prev)}
        className={triggerClassName}
        data-testid="custom-select-trigger"
      >
        <p className={triggerTextClassName}>{triggerText}</p>
        <span className="ml-2">
          <ChevronDownThinIcon />
        </span>
      </button>

      {isOpen && (
        <ul
          data-testid="custom-select-options"
          className="absolute z-10 flex flex-col gap-4 w-full bg-white border border-lightGray border-t-0  px-6 py-5"
        >
          {options.map((option: VariationValue, index: number) => {
            const { isAvailable, name, selected: isOptionSelected } = option

            return (
              <li key={name}>
                <button
                  data-testid={`custom-select-option-${index}`}
                  disabled={hasDisabledButtons ?? !isAvailable}
                  className="h-6 w-full flex justify-between items-center cursor-pointer"
                  onClick={() => handleSelect(option)}
                >
                  <p
                    className={`text-font-size-3 text-left
                  ${!isAvailable ? 'text-tertiary' : ''}
                  ${isOptionSelected ? 'text-primaryBlue' : ''}
                  ${
                    isAvailable && !isOptionSelected
                      ? 'text-restructure-primary'
                      : ''
                  }
                  `}
                  >
                    {name}
                  </p>
                  {!isAvailable && (
                    <p className="text-font-size-3 text-tertiary font-medium tracking-[-0.14px] ">
                      Fora de estoque
                    </p>
                  )}
                  {isOptionSelected && <CheckSelectedIcon />}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
